<template>
  <JMultiSelect
    v-model="internalModelValue"
    :error="error"
    :options="options"
    :isLoading="isLoading"
    :isLoadingMore="isFetching"
    searchable
    searchPlaceholder="Search destinations"
    disableFilter
    :compareFn="compare"
    @load-more="loadMore"
    @search="search"
  >
    <template #selected="{ item, index }">
      <div
        v-if="item"
        class="group relative flex cursor-default items-center gap-4 rounded-sm bg-gray-200 px-4 py-3"
      >
        <span v-if="typeof item !== 'string'" class="font-medium text-gray-500">
          {{ item.geoCity }}
          <span class="text-metadata font-medium">
            ({{ item.geoLocation }})
          </span>
        </span>
        <button
          class="absolute bottom-0 right-0 top-0 hidden bg-gray-200 group-hover:inline-block"
          title="Remove"
          type="button"
          @click.stop="remove(index)"
        >
          <JIcon name="close" size="16" />
        </button>
      </div>
    </template>

    <template #remaining-items="{ count }">
      <span class="rounded-sm bg-gray-200 px-4 py-3 font-medium text-gray-500">
        + {{ count }}
      </span>
    </template>

    <template #option="{ option }">
      <template v-if="typeof option === 'object'">
        <JIcon name="map-point-outline" class="text-gray-400" size="20" />

        <div class="ms-8 flex-1 [&>span]:block [&>span]:font-medium">
          <span class="mb-4 text-metadata-limited text-dark">
            <template v-if="option.locationType === 'Country'">
              {{ option.geoCountry }}
            </template>

            <template v-else-if="option.locationType === 'Continent'">
              {{ option.geoContinent }}
            </template>

            <template v-else>
              {{ option.geoCity }} ({{ option.geoLocation }})
            </template>
          </span>
          <span class="text-metadata text-gray-500">
            <template v-if="option.locationType === 'Country'">
              {{ option.geoContinent }}
            </template>

            <template v-else-if="option.locationType === 'Continent'">
              {{ option.locationType }}
            </template>

            <template v-else>
              {{ option.geoCountry }}
            </template>
          </span>
        </div>
      </template>
    </template>
  </JMultiSelect>
</template>

<script setup lang="ts">
import { useDebounceFn, useVModel } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { JIcon, JMultiSelect } from '@journey/ui-kit'
import { useDestinationsQuery } from '../../requests/queries'
import type { DestinationLocation } from '../../types/model/destination'
import type { Emits, Props } from './MultiDestinationSelectInput.types'

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  onlyCities: false,
  required: false,
})
const emit = defineEmits<Emits>()

// V-MODEL
const internalModelValue = useVModel(props, 'modelValue', emit)

const query = ref('')
const page = ref(1)
const size = ref(20)
const type = ref('City')
const excludedGeoTypes = ref(['S'])

const formattedQuery = computed(() =>
  query.value.length >= 3 ? query.value : ''
)

// DATA
const { data, isLoading, isFetching } = useDestinationsQuery(
  formattedQuery,
  page,
  size,
  excludedGeoTypes,
  type
)

const filteredData = computed(() => {
  let result: Array<DestinationLocation> | undefined

  result = data.value?.items

  if (props.omitOptions !== undefined && props.omitOptions.length) {
    result = result?.filter(
      (item) =>
        item.geoLocation && !props.omitOptions?.includes(item.geoLocation)
    )
  }

  return result
})

const options = ref<Array<DestinationLocation>>([])

watch(
  filteredData,
  (value) => {
    if (!value) {
      return
    }

    options.value = Array.isArray(options.value)
      ? [...options.value, ...value]
      : [...value]
  },
  {
    immediate: true,
  }
)

const search = useDebounceFn((value: string) => {
  options.value = []
  page.value = 1
  query.value = value
}, 500)

function loadMore() {
  if (isLoading.value || isFetching.value) {
    return
  }

  if (page.value === data.value?.page.total) {
    return
  }

  page.value += 1
}

function remove(index: number) {
  internalModelValue.value = Array.isArray(internalModelValue.value)
    ? [
        ...internalModelValue.value.slice(0, index),
        ...internalModelValue.value.slice(index + 1),
      ]
    : []
}

function compare(a: DestinationLocation, b: DestinationLocation): boolean {
  return a.geoLocation === b.geoLocation && a.geoCity === b.geoCity
}
</script>
